import { fork, put, all, call, takeLatest } from "redux-saga/effects";

import {
  getCustomersSuccess,
  getCustomersFailure,
  getMspBillingMetricsSuccess,
  getMspBillingMetricsFailure,
  getCustomerBillingMetricsSuccess,
  getCustomerBillingMetricsFailure,
  getBillingRulesSuccess,
  getBillingRulesFailure,
  getCustomerRulesSuccess,
  getCustomerRulesFailure,
  getCustomerMetadataSuccess,
  getCustomerMetadataFailure,
  createRulesSuccess,
  createRulesFailure,
  updateRuleSuccess,
  updateRuleFailure,
  updateRulesSuccess,
  updateRulesFailure,
  getBillingProfileSuccess,
  getBillingProfileFailure,
  updateBillingProfileSuccess,
  updateBillingProfileFailure,
  getCustomerInvoicesSuccess,
  getCustomerInvoicesFailure,
  getInvoiceRuleSnapshotsSuccess,
  getInvoiceRuleSnapshotsFailure,
} from "./billingManagementSlice";

import {
  getBillingCustomers as getBillingCustomersApi,
  getBillingMetrics as getBillingMetricsApi,
  getBillingRules as getBillingRulesApi,
  getCustomerBillingRules as getCustomerBillingRulesApi,
  getCustomerMetadata as getCustomerMetadataApi,
  createBillingRules as createBillingRulesApi,
  updateBillingRule as updateBillingRuleApi,
  updateBillingRules as updateBillingRulesApi,
  getBillingProfile as getBillingProfileApi,
  updateBillingProfile as updateBillingProfileApi,
  getCustomerInvoices as getCustomerInvoicesApi,
  getInvoiceRuleSnapshots as getInvoiceRuleSnapshotsApi,
} from "../../helpers/auth_aws_helper";

// 🛠️ Utility function to handle error responses consistently
const handleApiError = (error, operation) => {
  console.error(`Billing Management Saga: ${operation} Error =`, error);

  if (error.response) {
    // Server responded with error status
    const errorData = error.response.data;

    // Handle API Gateway specific errors
    if (errorData?.message) {
      if (errorData.message.includes("timeout") || errorData.message.includes("timed out")) {
        return "Request timed out. Please try again.";
      }
      if (errorData.message.includes("Endpoint request")) {
        return "Service temporarily unavailable. Please try again later.";
      }
      return errorData.message;
    }

    // Handle other error formats
    if (typeof errorData === "string") {
      return errorData;
    }
    if (errorData?.error) {
      return errorData.error;
    }
    if (errorData?.detail) {
      return errorData.detail;
    }

    return `Server error: ${error.response.status}`;
  } else if (error.request) {
    if (error.message && error.message.includes("timeout")) {
      return "Request timed out. Please check your connection and try again.";
    }
    return "No response received from server. Please check your connection.";
  } else {
    return error.message || "An unexpected error occurred.";
  }
};

// 🔄 Worker: Fetch Customers
function* fetchCustomers({ payload: params = {} }) {
  try {
    console.log("Billing Management Saga: Calling getBillingCustomers with params =", params);
    const response = yield call(getBillingCustomersApi, params);
    console.log("Billing Management Saga: Customers Response =", response);

    if (response.status === 200) {
      // The get function from aws_api_helper spreads response.data into the object
      // So response.items is at the top level, not response.data.items
      // Remove status from response to get the actual data
      const { status, ...responseData } = response;
      console.log("Billing Management Saga: Response Data (after removing status) =", responseData);
      console.log("Billing Management Saga: Items =", responseData.items);
      
      yield put(getCustomersSuccess(responseData));
    } else {
      const errorMessage = response.error || response.message || `Unexpected response: ${response.status}`;
      yield put(getCustomersFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Customers");
    yield put(getCustomersFailure(errorMessage));
  }
}

// MSP overview needs multiple metrics; API returns one metric per request. Fetch each and merge.
const MSP_OVERVIEW_METRICS = [
  "total_billed",
  "billed_customers",
  "net_usage_last_month",
  "fixed_charges_last_month",
  "ytd_total_billed",
  "net_usage_ytd",
  "fixed_charges_ytd",
];

// Customer scope metrics for customer invoices page.
const CUSTOMER_METRICS = [
  "total_billed",
  "total_invoices",
  "latest_invoice_amount",
  "yearly_invoice_trend",
];

// 🔄 Worker: Fetch MSP Billing Metrics
function* fetchMspBillingMetrics() {
  try {
    const scopeParams = { scope: "msp" };
    const merged = { start_date: null, end_date: null };
    // API returns metric-specific keys in data; map to unified keys for UI
    const metricKeyMap = {
      net_usage_ytd: "ytd_net_usage",
      fixed_charges_ytd: "ytd_fixed_charges",
      fixed_charges_last_month: "last_month_fixed_charges",
      net_usage_last_month: "last_month_net_usage",
    };
    for (const metric of MSP_OVERVIEW_METRICS) {
      try {
        const response = yield call(getBillingMetricsApi, metric, scopeParams);
        if (response && response.status === 200) {
          const { data, start_date, end_date } = response;
          if (data && typeof data === "object") {
            Object.assign(merged, data);
            const apiKey = metricKeyMap[metric];
            if (apiKey && data[apiKey] != null) merged[metric] = data[apiKey];
          }
          if (start_date) merged.start_date = merged.start_date || start_date;
          if (end_date) merged.end_date = merged.end_date || end_date;
        }
      } catch (_) {
        // Skip unsupported or failed metric; keep the rest
      }
    }
    yield put(getMspBillingMetricsSuccess(merged));
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch MSP Billing Metrics");
    yield put(getMspBillingMetricsFailure(errorMessage));
  }
}

// 🔄 Worker: Fetch Customer Billing Metrics (scope: customer; customer_orgcode required)
function* fetchCustomerBillingMetrics({ payload }) {
  const { customerOrgCode, year, month, start_date, end_date } = payload || {};
  if (!customerOrgCode) {
    yield put(getCustomerBillingMetricsFailure("Customer org code is required."));
    return;
  }
  try {
    const scopeParams = { scope: "customer", customer_orgcode: customerOrgCode };
    if (year != null) scopeParams.year = year;
    if (month != null) scopeParams.month = month;
    if (start_date) scopeParams.start_date = start_date;
    if (end_date) scopeParams.end_date = end_date;
    const merged = { start_date: null, end_date: null };
    for (const metric of CUSTOMER_METRICS) {
      try {
        const response = yield call(getBillingMetricsApi, metric, scopeParams);
        if (response && response.status === 200) {
          const { data, start_date: sd, end_date: ed } = response;
          if (data && typeof data === "object") Object.assign(merged, data);
          if (sd) merged.start_date = merged.start_date || sd;
          if (ed) merged.end_date = merged.end_date || ed;
        }
      } catch (_) {
        // Skip unsupported or failed metric
      }
    }
    yield put(getCustomerBillingMetricsSuccess(merged));
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Customer Billing Metrics");
    yield put(getCustomerBillingMetricsFailure(errorMessage));
  }
}

// 🔄 Worker: Fetch Billing Rules (Global)
function* fetchBillingRules({ payload: params = {} }) {
  try {
    console.log("Billing Management Saga: Calling getBillingRules with params =", params);
    const response = yield call(getBillingRulesApi, params);
    console.log("Billing Management Saga: Rules Response =", response);

    if (response.status === 200) {
      // Remove status from response to get the actual data
      const { status, ...responseData } = response;
      yield put(getBillingRulesSuccess(responseData));
    } else {
      const errorMessage = response.error || response.message || `Unexpected response: ${response.status}`;
      yield put(getBillingRulesFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Billing Rules");
    yield put(getBillingRulesFailure(errorMessage));
  }
}

// 🔄 Worker: Fetch Customer Rules
function* fetchCustomerRules({ payload: { customerOrgCode, params = {} } }) {
  try {
    console.log("Billing Management Saga: Calling getCustomerBillingRules with customerOrgCode =", customerOrgCode, "params =", params);
    const response = yield call(getCustomerBillingRulesApi, customerOrgCode, params);
    console.log("Billing Management Saga: Customer Rules Response =", response);

    if (response.status === 200) {
      // Remove status from response to get the actual data
      const { status, ...responseData } = response;
      yield put(getCustomerRulesSuccess(responseData));
    } else {
      const errorMessage = response.error || response.message || `Unexpected response: ${response.status}`;
      yield put(getCustomerRulesFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Customer Rules");
    yield put(getCustomerRulesFailure(errorMessage));
  }
}

// 🔄 Worker: Fetch Customer Metadata
function* fetchCustomerMetadata({ payload: { customerOrgCode, params = {} } }) {
  try {
    console.log("Billing Management Saga: Calling getCustomerMetadata with customerOrgCode =", customerOrgCode, "params =", params);
    const response = yield call(getCustomerMetadataApi, customerOrgCode, params);
    console.log("Billing Management Saga: Customer Metadata Response =", response);

    if (response.status === 200) {
      // Remove status from response to get the actual data
      const { status, ...responseData } = response;
      yield put(getCustomerMetadataSuccess(responseData));
    } else {
      const errorMessage = response.error || response.message || `Unexpected response: ${response.status}`;
      yield put(getCustomerMetadataFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Customer Metadata");
    yield put(getCustomerMetadataFailure(errorMessage));
  }
}

// 📄 Worker: Create Rules (Batch)
function* createBillingRules({ payload: { customerOrgCode, rules } }) {
  try {
    console.log("Billing Management Saga: Calling createBillingRules with customerOrgCode =", customerOrgCode, "rules =", rules);
    const response = yield call(createBillingRulesApi, customerOrgCode, rules);
    console.log("Billing Management Saga: Create Rules Response =", response);

    if (response.status === 201 || response.status === 200) {
      // POST returns response directly, so use response.data
      yield put(createRulesSuccess(response.data));
    } else {
      const errorMessage = response.data?.error || response.data?.message || `Unexpected response: ${response.status}`;
      yield put(createRulesFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Create Rules");
    yield put(createRulesFailure(errorMessage));
  }
}

// 📝 Worker: Update Rule
function* updateBillingRule({ payload: { customerOrgCode, ruleId, ruleData } }) {
  try {
    console.log("Billing Management Saga: Calling updateBillingRule with customerOrgCode =", customerOrgCode, "ruleId =", ruleId, "ruleData =", ruleData);
    const response = yield call(updateBillingRuleApi, customerOrgCode, ruleId, ruleData);
    console.log("Billing Management Saga: Update Rule Response =", response);

    if (response.status === 200) {
      // PUT spreads response.data, so remove status to get actual data
      const { status, ...responseData } = response;
      yield put(updateRuleSuccess(responseData));
    } else {
      const data = response.data || {};
      const detail = data.detail;
      const errorMessage =
        (Array.isArray(detail) ? detail.map((d) => d.msg || d.message || (typeof d === "string" ? d : JSON.stringify(d))).join(". ") : detail) ||
        data.error ||
        data.message ||
        response.error ||
        response.message ||
        `Unexpected response: ${response.status}`;
      yield put(updateRuleFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Update Rule");
    yield put(updateRuleFailure(errorMessage));
  }
}

// 📝 Worker: Update Rules (Batch)
function* updateBillingRules({ payload: { customerOrgCode, rules } }) {
  try {
    console.log("Billing Management Saga: Calling updateBillingRules with customerOrgCode =", customerOrgCode, "rules =", rules);
    const response = yield call(updateBillingRulesApi, customerOrgCode, rules);
    console.log("Billing Management Saga: Update Rules Response =", response);

    if (response.status === 200) {
      // PUT spreads response.data, so remove status to get actual data
      const { status, ...responseData } = response;
      yield put(updateRulesSuccess(responseData));
    } else {
      const data = response.data || {};
      const detail = data.detail;
      const errorMessage =
        (Array.isArray(detail) ? detail.map((d) => d.msg || d.message || (typeof d === "string" ? d : JSON.stringify(d))).join(". ") : detail) ||
        data.error ||
        data.message ||
        response.error ||
        response.message ||
        `Unexpected response: ${response.status}`;
      yield put(updateRulesFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Update Rules");
    yield put(updateRulesFailure(errorMessage));
  }
}

// 🔄 Worker: Fetch Billing Profile
function* fetchBillingProfile() {
  try {
    console.log("Billing Management Saga: Calling getBillingProfile");
    const response = yield call(getBillingProfileApi);
    console.log("Billing Management Saga: Billing Profile Response =", response);

    if (response.status === 200) {
      // Remove status from response to get the actual data
      const { status, ...responseData } = response;
      yield put(getBillingProfileSuccess(responseData));
    } else if (response.status === 404) {
      // Profile doesn't exist yet - this is okay, user can create one
      // Set profile to null so UI shows create mode
      yield put(getBillingProfileSuccess(null));
    } else {
      // Handle error response - extract error message from response
      const errorData = response.error || response.detail || response.message || `Unexpected response: ${response.status}`;
      yield put(getBillingProfileFailure(errorData));
    }
  } catch (error) {
    // Check if it's a 404 error (profile doesn't exist)
    if (error.response && error.response.status === 404) {
      // Profile doesn't exist - this is okay, allow user to create one
      yield put(getBillingProfileSuccess(null));
    } else {
      // Extract error message from error response
      let errorMessage = "Failed to fetch billing profile. Please try again.";
      if (error.response && error.response.data) {
        errorMessage = error.response.data.error || error.response.data.detail || error.response.data.message || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      yield put(getBillingProfileFailure(errorMessage));
    }
  }
}

// 📝 Worker: Update Billing Profile
function* updateBillingProfile({ payload: profileData }) {
  try {
    console.log("Billing Management Saga: Calling updateBillingProfile with profileData =", profileData);
    const response = yield call(updateBillingProfileApi, profileData);
    console.log("Billing Management Saga: Update Profile Response =", response);

    if (response.status === 200 || response.status === 201) {
      // PUT spreads response.data, so remove status to get actual data
      const { status, ...responseData } = response;
      yield put(updateBillingProfileSuccess(responseData));
    } else {
      // Pass full error body so UI can use getApiErrorMessage() for consistent formatting
      const errorBody = response.data != null ? response.data : { error: response.error || response.message || `Unexpected response: ${response.status}` };
      yield put(updateBillingProfileFailure(errorBody));
    }
  } catch (error) {
    // Pass full error body so UI can use getApiErrorMessage() for consistent formatting
    let payload = { message: "Failed to update billing profile. Please try again." };
    if (error.response && error.response.data != null) {
      payload = error.response.data;
    } else if (error.message) {
      payload = { message: error.message };
    }
    yield put(updateBillingProfileFailure(payload));
  }
}

// 👀 Watchers
function* watchGetCustomers() {
  yield takeLatest("billingManagement/getCustomers", fetchCustomers);
}

function* watchGetMspBillingMetrics() {
  yield takeLatest("billingManagement/getMspBillingMetrics", fetchMspBillingMetrics);
}

function* watchGetCustomerBillingMetrics() {
  yield takeLatest("billingManagement/getCustomerBillingMetrics", fetchCustomerBillingMetrics);
}

function* watchGetBillingRules() {
  yield takeLatest("billingManagement/getBillingRules", fetchBillingRules);
}

function* watchGetCustomerRules() {
  yield takeLatest("billingManagement/getCustomerRules", fetchCustomerRules);
}

function* watchGetCustomerMetadata() {
  yield takeLatest("billingManagement/getCustomerMetadata", fetchCustomerMetadata);
}

function* watchCreateRules() {
  yield takeLatest("billingManagement/createRules", createBillingRules);
}

function* watchUpdateRule() {
  yield takeLatest("billingManagement/updateRule", updateBillingRule);
}

function* watchUpdateRules() {
  yield takeLatest("billingManagement/updateRules", updateBillingRules);
}

function* watchGetBillingProfile() {
  yield takeLatest("billingManagement/getBillingProfile", fetchBillingProfile);
}

function* watchUpdateBillingProfile() {
  yield takeLatest("billingManagement/updateBillingProfile", updateBillingProfile);
}

// Worker: Fetch Customer Invoices
function* fetchCustomerInvoices({ payload }) {
  const { customerOrgCode, ...params } = payload || {};
  if (!customerOrgCode) {
    yield put(getCustomerInvoicesFailure("Customer org code is required."));
    return;
  }
  try {
    const response = yield call(getCustomerInvoicesApi, customerOrgCode, params);
    if (response.status === 200) {
      const { status, ...responseData } = response;
      yield put(getCustomerInvoicesSuccess(responseData));
    } else {
      const data = response.data || response;
      const errorMessage = data?.detail || data?.error || data?.message || `Unexpected response: ${response.status}`;
      yield put(getCustomerInvoicesFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = error.response?.data?.detail || error.response?.data?.error || handleApiError(error, "Fetch Customer Invoices");
    yield put(getCustomerInvoicesFailure(errorMessage));
  }
}

function* watchGetCustomerInvoices() {
  yield takeLatest("billingManagement/getCustomerInvoices", fetchCustomerInvoices);
}

// Worker: Fetch Invoice Rule Snapshots
function* fetchInvoiceRuleSnapshots({ payload }) {
  const { customerOrgCode, invoiceId } = payload || {};
  if (!customerOrgCode || !invoiceId) {
    yield put(getInvoiceRuleSnapshotsFailure("Customer org code and invoice ID are required."));
    return;
  }
  try {
    const response = yield call(getInvoiceRuleSnapshotsApi, customerOrgCode, invoiceId);
    if (response.status === 200) {
      const { status, ...responseData } = response;
      yield put(getInvoiceRuleSnapshotsSuccess(responseData));
    } else {
      const data = response.data || response;
      const errorMessage = data?.detail || data?.error || data?.message || `Unexpected response: ${response.status}`;
      yield put(getInvoiceRuleSnapshotsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = error.response?.data?.detail || error.response?.data?.error || handleApiError(error, "Fetch Invoice Rule Snapshots");
    yield put(getInvoiceRuleSnapshotsFailure(errorMessage));
  }
}

function* watchGetInvoiceRuleSnapshots() {
  yield takeLatest("billingManagement/getInvoiceRuleSnapshots", fetchInvoiceRuleSnapshots);
}

export function* billingManagementSaga() {
  yield all([
    fork(watchGetCustomers),
    fork(watchGetMspBillingMetrics),
    fork(watchGetCustomerBillingMetrics),
    fork(watchGetBillingRules),
    fork(watchGetCustomerRules),
    fork(watchGetCustomerMetadata),
    fork(watchCreateRules),
    fork(watchUpdateRule),
    fork(watchUpdateRules),
    fork(watchGetBillingProfile),
    fork(watchUpdateBillingProfile),
    fork(watchGetCustomerInvoices),
    fork(watchGetInvoiceRuleSnapshots),
  ]);
}

export default billingManagementSaga;
